import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store";
import {
  startSignalRConnection,
  stopSignalRConnection,
  joinSignalRRoom,
  leaveSignalRRoom,
} from "./thunks";
import { log } from "./thunks/shared";

export type SignalRConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "error";

interface SignalRState {
  connectionId: string | null;
  status: SignalRConnectionStatus;
  hub: string | null;
  lastError: string | null;
  rooms: string[];
  lastDeniedRoomId: string | null;
  lastJoinedRoomId: string | null;
  fileScanResult: { isMalicious: boolean } | null;
}

const initialState: SignalRState = {
  connectionId: null,
  status: "disconnected",
  hub: null,
  lastError: null,
  rooms: [],
  lastDeniedRoomId: null,
  lastJoinedRoomId: null,
  fileScanResult: null,
};

const signalrSlice = createSlice({
  name: "signalr",
  initialState,
  reducers: {
    connectionIdUpdated: (state, action: PayloadAction<string>) => {
      state.connectionId = action.payload;
      log("reducer: connectionIdUpdated", { connectionId: action.payload });
    },
    roomAccessDenied: (state, action: PayloadAction<string>) => {
      state.lastDeniedRoomId = action.payload;
      log("reducer: roomAccessDenied", { roomId: action.payload });
    },
    roomJoined: (state, action: PayloadAction<string>) => {
      state.lastJoinedRoomId = action.payload;
      if (!state.rooms.includes(action.payload)) {
        state.rooms.push(action.payload);
      }
      log("reducer: roomJoined", { roomId: action.payload });
    },
    clearSignalRErrors: (state) => {
      state.lastError = null;
      log("reducer: clearSignalRErrors");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startSignalRConnection.pending, (state) => {
        state.status = "connecting";
        state.lastError = null;
      })
      .addCase(startSignalRConnection.fulfilled, (state, action) => {
        state.status = "connected";
        state.hub = action.payload.hub;
      })
      .addCase(startSignalRConnection.rejected, (state, action) => {
        state.status = "error";
        state.lastError = action.payload || null;
      })
      .addCase(stopSignalRConnection.fulfilled, (state) => {
        state.status = "disconnected";
        state.hub = null;
        state.rooms = [];
      })
      .addCase(stopSignalRConnection.rejected, (state, action) => {
        state.status = "error";
        state.lastError = action.payload || null;
      })
      .addCase(joinSignalRRoom.fulfilled, (state, action) => {
        if (!state.rooms.includes(action.payload.roomId)) {
          state.rooms.push(action.payload.roomId);
        }
      })
      .addCase(joinSignalRRoom.rejected, (state, action) => {
        state.lastError = action.payload || null;
      })
      .addCase(leaveSignalRRoom.fulfilled, (state, action) => {
        state.rooms = state.rooms.filter((r) => r !== action.payload.roomId);
      })
      .addCase(leaveSignalRRoom.rejected, (state, action) => {
        state.lastError = action.payload || null;
      });
  },
});

export const {
  connectionIdUpdated,
  roomAccessDenied,
  roomJoined,
  clearSignalRErrors,
} = signalrSlice.actions;
export const signalrReducer = signalrSlice.reducer;

export {
  startSignalRConnection,
  stopSignalRConnection,
  restartSignalRConnection,
  joinSignalRRoom,
  leaveSignalRRoom,
  additionalInformationUpdated,
  hasSignalRConnectionInstance,
} from "./thunks";

// Selectors
export const selectSignalRConnectionId = (state: RootState) =>
  state.signalr.connectionId;
export const selectSignalRStatus = (state: RootState) => state.signalr.status;
export const selectIsSignalRConnected = (state: RootState) =>
  state.signalr.status === "connected";
export const selectIsSignalRConnecting = (state: RootState) =>
  state.signalr.status === "connecting";
export const selectJoinedRooms = (state: RootState) => state.signalr.rooms;
export const selectSignalRError = (state: RootState) => state.signalr.lastError;
export const selectLastDeniedRoom = (state: RootState) =>
  state.signalr.lastDeniedRoomId;
export const selectLastJoinedRoom = (state: RootState) =>
  state.signalr.lastJoinedRoomId;
export const selectFileScanResult = (state: RootState) =>
  state.signalr.fileScanResult;
